import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { getAuth } from 'firebase/auth';
import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase';

const NEWS_COLLECTION = 'newsPosts';
const PUBLIC_NEWS_BASE_URL = 'https://ubunifucollege.com/view-post.html?slug=';
const QUILL_CSS_URL = '/vendor/quill/quill.snow.css';
const QUILL_JS_URL = '/vendor/quill/quill.min.js';
const QUILL_LOAD_TIMEOUT_MS = 4000;

const emptyForm = {
  title: '',
  slug: '',
  category: 'Updates',
  authorName: 'Ubunifu College',
  status: 'draft',
  coverImage: '',
  excerpt: '',
  content: '',
  tags: '',
  seoTitle: '',
  seoDescription: '',
  ogImage: '',
};

let quillLoadPromise = null;

const loadScriptOnce = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    if (existing.dataset.loaded === 'true') {
      resolve();
      return;
    }
    existing.addEventListener('load', resolve, { once: true });
    existing.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.onload = () => {
    script.dataset.loaded = 'true';
    resolve();
  };
  script.onerror = reject;
  document.body.appendChild(script);
});

const loadStylesheetOnce = (href) => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

const loadQuill = () => {
  if (window.Quill) return Promise.resolve();
  if (!quillLoadPromise) {
    loadStylesheetOnce(QUILL_CSS_URL);
    quillLoadPromise = Promise.race([
      loadScriptOnce(QUILL_JS_URL),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error('Quill editor timed out while loading.')), QUILL_LOAD_TIMEOUT_MS);
      }),
    ]);
  }
  return quillLoadPromise;
};

const slugify = (value) => (
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90)
);

const normalizeTags = (value) => (
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 12)
);

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const stripHtml = (value) => (
  String(value || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const safeFileName = (fileName) => (
  String(fileName || 'image')
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'image'
);

const postToForm = (post) => ({
  title: post.title || '',
  slug: post.slug || '',
  category: post.category || 'Updates',
  authorName: post.authorName || 'Ubunifu College',
  status: post.status === 'published' || post.published ? 'published' : 'draft',
  coverImage: post.coverImage || '',
  excerpt: post.excerpt || '',
  content: post.content || '',
  tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
  seoTitle: post.seoTitle || '',
  seoDescription: post.seoDescription || '',
  ogImage: post.ogImage || '',
});

function NewsPostEditorComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEditing = Boolean(postId);
  const editorContainerRef = useRef(null);
  const quillRef = useRef(null);
  const coverInputRef = useRef(null);
  const formRef = useRef(emptyForm);
  const editorImageHandlerRef = useRef(null);

  const [formData, setFormData] = useState(emptyForm);
  const [existingPost, setExistingPost] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [editorReady, setEditorReady] = useState(false);
  const [editorLoadFailed, setEditorLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const editorInitTimeoutRef = useRef(null);

  useEffect(() => {
    formRef.current = formData;
  }, [formData]);

  const currentPublicUrl = useMemo(() => {
    const slug = slugify(formData.slug || formData.title);
    return slug ? `${PUBLIC_NEWS_BASE_URL}${encodeURIComponent(slug)}` : '';
  }, [formData.slug, formData.title]);

  const uploadNewsImage = useCallback(async (file, section) => {
    if (!file) return '';

    if (!file.type.startsWith('image/')) {
      swal('Image required', 'Please choose an image file.', 'warning');
      return '';
    }

    const maxBytes = 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      swal('Image too large', 'Please upload an image smaller than 8MB.', 'warning');
      return '';
    }

    const activeSlug = slugify(formRef.current.slug || formRef.current.title) || 'draft';
    const path = `news-posts/${section}/${activeSlug}/${Date.now()}-${safeFileName(file.name)}`;
    const fileRef = storageRef(storage, path);

    await uploadBytes(fileRef, file, {
      contentType: file.type,
      customMetadata: {
        uploadedBy: auth.currentUser?.uid || 'unknown',
        source: 'news-post-editor',
      },
    });

    return getDownloadURL(fileRef);
  }, [auth]);

  editorImageHandlerRef.current = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file || !quillRef.current) return;

      setUploadingInline(true);
      try {
        const imageUrl = await uploadNewsImage(file, 'article-images');
        if (!imageUrl) return;

        const quill = quillRef.current;
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, 'image', imageUrl, 'user');
        quill.setSelection(index + 1, 0, 'silent');
        setFormData((current) => ({ ...current, content: quill.root.innerHTML }));
      } catch (error) {
        console.error('Failed to upload article image:', error);
        swal('Upload failed', error.message || 'Could not upload the image.', 'error');
      } finally {
        setUploadingInline(false);
      }
    };
    input.click();
  };

  useEffect(() => {
    let cancelled = false;

    const loadPost = async () => {
      if (!isEditing) {
        setLoading(false);
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, NEWS_COLLECTION, postId));
        if (!snapshot.exists()) {
          swal('Post not found', 'This news post could not be found.', 'error');
          navigate('/news-posts');
          return;
        }

        const post = { docID: snapshot.id, ...snapshot.data() };
        if (!cancelled) {
          setExistingPost(post);
          setFormData(postToForm(post));
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load news post:', error);
        if (!cancelled) {
          setLoading(false);
          swal('Could not load post', error.message || 'Please try again.', 'error');
        }
      }
    };

    loadPost();

    return () => {
      cancelled = true;
    };
  }, [isEditing, navigate, postId]);

  useEffect(() => {
    let cancelled = false;

    if (editorInitTimeoutRef.current) {
      window.clearTimeout(editorInitTimeoutRef.current);
    }

    editorInitTimeoutRef.current = window.setTimeout(() => {
      if (!cancelled && !editorReady && !quillRef.current) {
        quillLoadPromise = null;
        setEditorLoadFailed(true);
      }
    }, QUILL_LOAD_TIMEOUT_MS);

    loadQuill().then(() => {
      if (cancelled || !editorContainerRef.current || quillRef.current) return;

      const toolbarOptions = [
        [{ header: [1, 2, 3, 4, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ];

      const quill = new window.Quill(editorContainerRef.current, {
        theme: 'snow',
        placeholder: 'Write the full article here. Use the image button to upload images into Firebase Storage.',
        modules: {
          toolbar: {
            container: toolbarOptions,
            handlers: {
              image: () => editorImageHandlerRef.current?.(),
            },
          },
        },
      });

      quillRef.current = quill;
      quill.clipboard.dangerouslyPasteHTML(formRef.current.content || '');
      quill.on('text-change', () => {
        setFormData((current) => ({ ...current, content: quill.root.innerHTML }));
      });
      setEditorReady(true);
    }).catch((error) => {
      console.error('Failed to load editor:', error);
      if (!cancelled) {
        setEditorLoadFailed(true);
      }
      swal('Editor failed to load', 'Please check your connection and refresh this page.', 'error');
    });

    return () => {
      cancelled = true;
      if (editorInitTimeoutRef.current) {
        window.clearTimeout(editorInitTimeoutRef.current);
      }
    };
  }, [editorReady]);

  useEffect(() => {
    if (!quillRef.current || !editorReady || loading) return;
    if (quillRef.current.root.innerHTML === formData.content) return;
    quillRef.current.clipboard.dangerouslyPasteHTML(formData.content || '');
  }, [editorReady, formData.content, loading]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => {
      const next = { ...current, [name]: value };

      if (name === 'title' && (!isEditing || current.slug === '' || current.slug === slugify(current.title))) {
        next.slug = slugify(value);
      }

      if (name === 'coverImage' && !current.ogImage) {
        next.ogImage = value;
      }

      return next;
    });
  };

  const handleCoverFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingCover(true);
    try {
      const imageUrl = await uploadNewsImage(file, 'covers');
      if (!imageUrl) return;

      setFormData((current) => ({
        ...current,
        coverImage: imageUrl,
        ogImage: current.ogImage || imageUrl,
      }));
      swal('Cover uploaded', 'The cover image has been uploaded and attached.', 'success');
    } catch (error) {
      console.error('Failed to upload cover image:', error);
      swal('Upload failed', error.message || 'Could not upload the cover image.', 'error');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const title = formData.title.trim();
    const slug = slugify(formData.slug || title);

    if (!title) {
      swal('Title required', 'Give this post a clear title before saving.', 'warning');
      return;
    }

    if (!slug) {
      swal('Slug required', 'Add a URL slug for this post.', 'warning');
      return;
    }

    setSaving(true);

    try {
      const postRef = doc(db, NEWS_COLLECTION, slug);
      const slugChanged = Boolean(isEditing && postId !== slug);

      if (!isEditing || slugChanged) {
        const existingDoc = await getDoc(postRef);
        if (existingDoc.exists()) {
          swal('Slug already used', 'Choose a different slug so each article has a unique URL.', 'warning');
          setSaving(false);
          return;
        }
      }

      const nowMs = Date.now();
      const status = formData.status === 'published' ? 'published' : 'draft';
      const wasPublished = Boolean(existingPost?.publishedAtMs || toMillis(existingPost?.publishedAt));
      const publishedAtMs = status === 'published'
        ? (wasPublished ? (existingPost.publishedAtMs || toMillis(existingPost.publishedAt)) : nowMs)
        : null;
      const createdAtMs = existingPost?.createdAtMs || toMillis(existingPost?.createdAt) || nowMs;
      const coverImage = formData.coverImage.trim();
      const content = formData.content.trim();
      const excerpt = formData.excerpt.trim() || stripHtml(content).slice(0, 180);

      const payload = {
        title,
        slug,
        category: formData.category.trim() || 'Updates',
        authorName: formData.authorName.trim() || 'Ubunifu College',
        status,
        published: status === 'published',
        coverImage,
        excerpt,
        content,
        contentFormat: 'html',
        tags: normalizeTags(formData.tags),
        seoTitle: formData.seoTitle.trim(),
        seoDescription: formData.seoDescription.trim() || excerpt,
        ogImage: formData.ogImage.trim() || coverImage,
        canonicalPath: `view-post.html?slug=${slug}`,
        createdBy: existingPost?.createdBy || auth.currentUser?.uid || 'unknown',
        updatedBy: auth.currentUser?.uid || 'unknown',
        createdAtMs,
        updatedAtMs: nowMs,
        publishedAtMs,
        updatedAt: serverTimestamp(),
        publishedAt: publishedAtMs ? Timestamp.fromMillis(publishedAtMs) : null,
      };

      if (!isEditing) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(postRef, payload, { merge: true });

      if (slugChanged) {
        await deleteDoc(doc(db, NEWS_COLLECTION, postId));
      }

      swal('Saved', status === 'published' ? 'The post is published on the website.' : 'The draft has been saved.', 'success');
      navigate('/news-posts');
    } catch (error) {
      console.error('Failed to save news post:', error);
      swal('Could not save post', error.message || 'Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='dashComp newsAdminPage'>
        <div className='newsAdminLoading newsEditorInitialLoading'>
          <Spinner animation='border' size='sm' />
          <span>Loading post...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='dashComp newsAdminPage newsEditorPage'>
      <form onSubmit={handleSubmit}>
        <section className='newsEditorTop'>
          <div>
            <button type='button' className='newsEditorBack' onClick={() => navigate('/news-posts')}>
              Back to posts
            </button>
            <span className='newsAdminEyebrow'>Website CMS</span>
            <h2>{isEditing ? 'Edit Post' : 'Add Post'}</h2>
            <p>Write, format, upload images, and publish a website update.</p>
          </div>
          <div className='newsEditorTopActions'>
            {currentPublicUrl && (
              <a href={currentPublicUrl} target='_blank' rel='noreferrer' className='newsAdminPreviewLink'>
                Open public URL
              </a>
            )}
            <Button variant='dark' type='submit' disabled={saving || uploadingCover || uploadingInline}>
              {saving ? <Spinner animation='border' size='sm' /> : null}
              <span>{saving ? 'Saving...' : 'Save Post'}</span>
            </Button>
          </div>
        </section>

        <section className='newsEditorLayout'>
          <div className='newsEditorMainPanel'>
            <Form.Group className='newsAdminField newsAdminFieldWide'>
              <Form.Label>Title</Form.Label>
              <Form.Control name='title' value={formData.title} onChange={handleChange} placeholder='Example: Ubunifu students launch a new AI project' required />
            </Form.Group>

            <Form.Group className='newsAdminField newsAdminFieldWide'>
              <Form.Label>Excerpt</Form.Label>
              <Form.Control as='textarea' rows={3} name='excerpt' value={formData.excerpt} onChange={handleChange} placeholder='Short summary shown on the updates page and social previews. If empty, it will be generated from the article body.' />
            </Form.Group>

            <div className='newsAdminField newsAdminFieldWide'>
              <label>Article body</label>
              <div className='newsEditorToolbarHint'>
                Use the image button in the toolbar to upload article images into Firebase Storage.
                {uploadingInline && <span> Uploading image...</span>}
              </div>
              <div className='newsQuillShell'>
                {!editorReady && !editorLoadFailed && (
                  <div className='newsEditorLoadingOverlay'>
                    <Spinner animation='border' size='sm' />
                    <span>Loading editor...</span>
                  </div>
                )}
                {editorLoadFailed ? (
                  <Form.Control
                    as='textarea'
                    rows={16}
                    name='content'
                    value={formData.content}
                    onChange={handleChange}
                    placeholder='Write the full article here.'
                    className='newsQuillFallback'
                  />
                ) : (
                  <div ref={editorContainerRef} className='newsQuillEditor' />
                )}
              </div>
            </div>
          </div>

          <aside className='newsEditorSidePanel'>
            <div className='newsEditorSideBlock'>
              <h4>Publish</h4>
              <Form.Group className='newsAdminField'>
                <Form.Label>Status</Form.Label>
                <Form.Select name='status' value={formData.status} onChange={handleChange}>
                  <option value='draft'>Draft</option>
                  <option value='published'>Published</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className='newsAdminField'>
                <Form.Label>Slug</Form.Label>
                <Form.Control name='slug' value={formData.slug} onChange={handleChange} placeholder='student-ai-project-launch' required />
              </Form.Group>
              <Form.Group className='newsAdminField'>
                <Form.Label>Category</Form.Label>
                <Form.Control name='category' value={formData.category} onChange={handleChange} placeholder='Updates' />
              </Form.Group>
              <Form.Group className='newsAdminField'>
                <Form.Label>Author</Form.Label>
                <Form.Control name='authorName' value={formData.authorName} onChange={handleChange} placeholder='Ubunifu College' />
              </Form.Group>
              <Form.Group className='newsAdminField'>
                <Form.Label>Tags</Form.Label>
                <Form.Control name='tags' value={formData.tags} onChange={handleChange} placeholder='Students, AI, Events' />
              </Form.Group>
            </div>

            <div className='newsEditorSideBlock'>
              <h4>Cover image</h4>
              {formData.coverImage && (
                <img src={formData.coverImage} alt='' className='newsCoverPreview' />
              )}
              <Form.Group className='newsAdminField'>
                <Form.Label>Cover image URL</Form.Label>
                <Form.Control name='coverImage' value={formData.coverImage} onChange={handleChange} placeholder='https://...' />
              </Form.Group>
              <input ref={coverInputRef} type='file' accept='image/*' className='d-none' onChange={handleCoverFileChange} />
              <Button
                variant='outline-dark'
                type='button'
                className='newsUploadButton'
                disabled={uploadingCover}
                onClick={() => coverInputRef.current?.click()}
              >
                {uploadingCover ? <Spinner animation='border' size='sm' /> : null}
                <span>{uploadingCover ? 'Uploading...' : 'Upload cover image'}</span>
              </Button>
            </div>

            <div className='newsEditorSideBlock'>
              <h4>SEO & social</h4>
              <Form.Group className='newsAdminField'>
                <Form.Label>SEO title</Form.Label>
                <Form.Control name='seoTitle' value={formData.seoTitle} onChange={handleChange} placeholder='Leave blank to use the post title' />
              </Form.Group>
              <Form.Group className='newsAdminField'>
                <Form.Label>SEO description</Form.Label>
                <Form.Control as='textarea' rows={3} name='seoDescription' value={formData.seoDescription} onChange={handleChange} placeholder='Leave blank to use the excerpt' />
              </Form.Group>
              <Form.Group className='newsAdminField'>
                <Form.Label>Social image URL</Form.Label>
                <Form.Control name='ogImage' value={formData.ogImage} onChange={handleChange} placeholder='Leave blank to use the cover image' />
              </Form.Group>
            </div>
          </aside>
        </section>
      </form>
    </div>
  );
}

export default NewsPostEditorComp;
